import React, { useCallback, useRef, useState } from "react";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { ArrowLeftToLineIcon, ArrowRightToLineIcon, Users } from "lucide-react";

import { ProfileMenu } from "./ProfileMenu";
import { cn } from "@/lib/utils";
import { createBase64Image, defaultUserAvatar } from "@/helpers/images";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { NavigationSidebarItemsList } from "@/components/sidebar/Sidebar.const";
import { Link } from "react-router";
import { NavigationMenuList } from "@/components/sidebar/NavigationMenuList";

interface SidebarNavProps {
  isPinned: boolean;
  onPinChange: (isPinned: boolean) => void;
}

export const SidebarNav: React.FC<SidebarNavProps> = ({ isPinned, onPinChange }) => {
  const [isHovered, setIsHovered] = useState(false);
  const user = useAuthenticatedUser();
  const hoverTimeout = useRef<number | null>(null);
  const userData = useAuthenticatedUser();

  const togglePin = useCallback(() => {
    onPinChange(!isPinned);
  }, [isPinned, onPinChange]);

  const handleMouseEnter = () => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (!isPinned) {
      hoverTimeout.current = setTimeout(() => {
        setIsHovered(false);
      }, 200);
    }
  };

  const avatarSrc = userData.avatar ? createBase64Image(userData.avatar) : defaultUserAvatar;

  return (
    <div className="relative flex">
      {/* Sidebar container */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-72",
          "transition-all duration-300 ease-in-out",
          isPinned || isHovered ? "translate-x-0" : "-translate-x-[calc(100%-56px)]",
          isPinned || isHovered ? "backdrop-blur-md" : "backdrop-blur-md",
          isPinned || isHovered ? "bg-ui-sidebar-background-rgba" : "bg-whites",
        )}
        style={
          {
            "--tw-bg-opacity": isPinned || isHovered ? "1" : "0.2",
          } as React.CSSProperties
        }
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Sidebar content */}
        <div
          className={`text-atriablue-500 grid h-full grid-rows-[auto,1fr,auto] overflow-y-auto ${
            isPinned || isHovered ? "shadow-md" : ""
          }`}
        >
          {/* Logo and pin/unpin button container */}
          <div className="flex items-center justify-between px-4 pb-2 pt-10">
            <div className="flex-shrink-0">
              <img src="/images/pathfinder_logo_beta.svg" alt="Pathfinder logo" width={160} height={110} />
            </div>
            {(isPinned || isHovered) && (
              <button
                type="button"
                className="rounded-full p-2 text-gray-400 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-500"
                onClick={togglePin}
                aria-label={isPinned ? "Unpin sidebar" : "Pin sidebar"}
              >
                {isPinned ? (
                  <ArrowLeftToLineIcon className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <ArrowRightToLineIcon className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            )}
          </div>
          {/* Navigation content */}
          <div className={`flex flex-col px-2 ${!isPinned && !isHovered ? "invisible" : ""}`}>
            <div className="flex h-full flex-col">
              <nav className="flex-1">
                <NavigationMenuList navigation={NavigationSidebarItemsList} />
              </nav>
              <div className="mb-4 mt-auto">
                {userData.features.includes("user_admin") && (
                  <Link
                    to="/admin/users"
                    className="text-atria blue-500 group flex items-center gap-x-2 rounded-md px-2 py-1.5 text-sm font-semibold leading-6 transition-colors duration-200 ease-in-out hover:bg-white hover:text-gray-600"
                  >
                    <Users className="h-5 w-5 shrink-0" aria-hidden="true" />
                    <span>Users</span>
                  </Link>
                )}
                <ProfileMenu avatarSrc={avatarSrc} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Always visible container for icons when sidebar is closed */}
      {!isPinned && !isHovered && (
        <div className="fixed inset-y-0 left-0 z-50 flex w-20 flex-col items-center" onMouseEnter={handleMouseEnter}>
          <div className="flex h-[88px] w-full items-center justify-start px-0 pt-4">
            <button
              className="mt-[-30px] rounded-md p-4 text-gray-500 hover:bg-gray-100 hover:text-gray-600"
              onClick={() => setIsHovered(true)}
              aria-label="Open sidebar"
            >
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="flex-grow" />
          <div className="pointer-events-auto ml-0 flex w-full flex-col space-y-4 pb-4 pl-4">
            <button className="overflow-hidden rounded-full p-1.5" onClick={() => setIsHovered(true)}>
              <img src={avatarSrc} alt={user.username} width={24} height={24} className="rounded-full" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
